import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router';
import SignatureCanvas from 'react-signature-canvas';
import { enqueueSnackbar } from 'notistack';
import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';
import axios, { endpoints, formatEndpoint, INTERNAL_SERVER_ERROR } from 'src/utils/axios';
import { downscaleImageFile } from 'src/pages/app/rentals/trailers/downscale-image';
import useModelData from 'src/hooks/use-model-data';
import { DisplayPageData } from 'src/components/display-page-data';
import { HOST_API } from 'src/config-global';
import { paths } from 'src/routes/paths';

type ContractInfoResponse = {
  status: 'pending' | 'expired' | 'signed' | 'awaitingConfirmation';
  companyName: string;
  companyLogoUrl: string;
  customerFirstName: string;
  organizationId: number;
  customerIdPhotoCount: number;
  // standalone agreement flow only: fields the customer must fill in to sign
  signerFields?: string[];
  signerFieldValues?: Record<string, string> | null;
  rejectionReason: string;
};

type PageModel = {
  contractInfo: ContractInfoResponse;
};

export function BrandBar({ companyName, companyLogoUrl }: { companyName?: string; companyLogoUrl?: string }) {
  return (
    <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" py={1.5}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            {companyLogoUrl ? (
              <Box component="img" src={companyLogoUrl} sx={{ height: 36, borderRadius: 0.5 }} />
            ) : (<>
              <Logo dark />
              <Typography variant="subtitle1">{companyName}</Typography>
            </>)}
          </Stack>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', letterSpacing: '0.12em', display: { xs: 'none', sm: 'block' } }}
          >
            Rental Agreement
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

export function StatusScreen({
  icon,
  iconColor,
  title,
  message,
  action,
}: {
  icon: string;
  iconColor: string;
  title: string;
  message: string;
  action?: React.ReactNode;
}) {
  return (
    <Box minHeight="70vh" display="flex" alignItems="center" justifyContent="center" px={2}>
      <Stack alignItems="center" gap={2} maxWidth={480} textAlign="center">
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Iconify icon={icon} width={36} />
        </Box>
        <Typography variant="h4" sx={{ textWrap: 'balance' }}>
          {title}
        </Typography>
        <Typography color="text.secondary">{message}</Typography>
        {action}
      </Stack>
    </Box>
  );
}

// the same page signs both documents: the outbound contract (legacy combined
// flow) and the standalone lease agreement sent earlier in the staged flow
export default function Page({ agreement }: { agreement?: boolean }) {
  const { signatureUuid } = useParams();

  const findEndpoint = agreement
    ? formatEndpoint(endpoints.agreementInfo.findByUuid, { agreementUuid: signatureUuid })
    : formatEndpoint(endpoints.contractInfo.findBySignatureUuid, { signatureUuid });

  const dataLoader = useCallback(
    () => ({
      contractInfo: axios.get(findEndpoint).then((response) => response.data),
    }),
    [findEndpoint]
  );

  const { data, loaded, error, loading } = useModelData<PageModel>(dataLoader);

  const sigCanvas = useRef<SignatureCanvas>(null);
  const [agreed, setAgreed] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneSigning, setDoneSigning] = useState(false);
  const [uploadedIdCount, setUploadedIdCount] = useState<number | null>(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [signerValues, setSignerValues] = useState<Record<string, string>>({});

  const contractInfo = data?.contractInfo;
  const idPhotoCount = uploadedIdCount ?? contractInfo?.customerIdPhotoCount ?? 0;
  const signerFields = contractInfo?.signerFields || [];
  // values already on the rental (a re-sign after rejection) prefill the inputs
  const signerValue = (name: string) =>
    signerValues[name] ?? contractInfo?.signerFieldValues?.[name] ?? '';
  const allSignerFieldsFilled = signerFields.every((name) => signerValue(name).trim() !== '');

  const pdfUrl = HOST_API + (agreement
    ? formatEndpoint(endpoints.agreementInfo.pdf, { agreementUuid: signatureUuid })
    : formatEndpoint(endpoints.contractInfo.pdf, { signatureUuid }));
  const uploadIdPhotoUrl = agreement
    ? formatEndpoint(endpoints.agreementInfo.uploadIdPhoto, { agreementUuid: signatureUuid })
    : formatEndpoint(endpoints.contractInfo.uploadIdPhoto, { signatureUuid });
  const canvasWidth = Math.min(500, (typeof window !== 'undefined' ? window.innerWidth : 560) - 96);

  const uploadIdPhoto = (file: File) => {
    setUploadingId(true);
    // phone camera shots are many megabytes and the customer is often on
    // cellular — shrink before uploading, the server stores 1600px anyway
    downscaleImageFile(file)
      .then((downscaled) =>
        axios.postForm(uploadIdPhotoUrl, {
          file: downscaled,
        })
      )
      .then((response) => setUploadedIdCount(response.data.customerIdPhotoCount))
      .catch(() => {
        enqueueSnackbar('Your photo could not be uploaded. Please try again.', { variant: 'error' });
      })
      .finally(() => setUploadingId(false));
  };

  const submitSignature = () => {
    if (idPhotoCount === 0) {
      enqueueSnackbar('Please upload a photo of your ID first', { variant: 'error' });
      return;
    }
    if (!allSignerFieldsFilled) {
      enqueueSnackbar('Please fill in your information first', { variant: 'error' });
      return;
    }
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      enqueueSnackbar('Please draw your signature first', { variant: 'error' });
      return;
    }
    setSubmitting(true);
    const signaturePngBase64 = sigCanvas.current.getCanvas().toDataURL('image/png');
    const signerFieldValues = Object.fromEntries(
      signerFields.map((name) => [name, signerValue(name).trim()])
    );
    axios
      .post(
        agreement ? endpoints.agreementInfo.submitSignature : endpoints.contractInfo.submitSignature,
        agreement
          ? { agreementUuid: signatureUuid, signaturePngBase64, signerFieldValues }
          : { signatureUuid, signaturePngBase64 }
      )
      .then(() => {
        setSignatureOpen(false);
        setDoneSigning(true);
      })
      .catch((submitError) => {
        if (submitError?.response?.status === 422 && submitError.response.data?.length) {
          submitError.response.data.forEach((validation: { message: string }) =>
            enqueueSnackbar(validation.message, { variant: 'error' })
          );
        } else {
          enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: 'error' });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" sx={{ bgcolor: 'grey.100' }}>
      <BrandBar companyName={contractInfo?.companyName} companyLogoUrl={contractInfo?.companyLogoUrl} />

      <Box flexGrow={1}>
        <DisplayPageData loading={loading} error={error} data={data}>
          {doneSigning && (
            <StatusScreen
              icon="solar:check-circle-bold"
              iconColor="success.main"
              title="Agreement signed"
              message="Your signature and ID are being reviewed by the rental company. You'll receive the signed contract by email once the rental is confirmed."
              action={
                <Button
                  variant="contained"
                  color="success"
                  href={
                    contractInfo?.organizationId
                      ? `${paths.rentalsPortal.login}?org=${contractInfo.organizationId}`
                      : paths.rentalsPortal.login
                  }
                >
                  Go to Customer Portal
                </Button>
              }
            />
          )}
          {!doneSigning && loaded && contractInfo?.status === 'expired' && (
            <StatusScreen
              icon="solar:clock-circle-bold"
              iconColor="warning.main"
              title="This signing link has expired"
              message={`Please contact ${contractInfo.companyName || 'the rental company'} to request a new link.`}
            />
          )}
          {!doneSigning && loaded && contractInfo?.status === 'signed' && (
            <StatusScreen
              icon="solar:check-circle-bold"
              iconColor="success.main"
              title="Already signed"
              message="This contract has been signed — a copy was emailed to you."
            />
          )}
          {!doneSigning && loaded && contractInfo?.status === 'awaitingConfirmation' && (
            <StatusScreen
              icon="solar:clock-circle-bold"
              iconColor="info.main"
              title="Waiting for confirmation"
              message="You've signed and uploaded your ID — the rental company is reviewing your submission. You'll receive the signed contract by email once the rental is confirmed."
            />
          )}
          {!doneSigning && loaded && contractInfo?.status === 'pending' && (
            <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
              <Stack gap={2}>
                <Box>
                  <Typography variant="h4" sx={{ textWrap: 'balance' }}>
                    {contractInfo.customerFirstName
                      ? `Hi ${contractInfo.customerFirstName}, your rental agreement is ready`
                      : 'Your rental agreement is ready'}
                  </Typography>
                  <Typography color="text.secondary" pt={0.5}>
                    Review the agreement below, upload a photo of your ID, then sign to complete your
                    rental.
                  </Typography>
                </Box>

                {!!contractInfo.rejectionReason && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: 'error.lighter',
                      border: '1px solid',
                      borderColor: 'error.light',
                    }}
                  >
                    <Typography variant="subtitle2" color="error.dark">
                      Your previous submission was not accepted
                    </Typography>
                    <Typography variant="body2" color="error.dark">
                      {`${contractInfo.rejectionReason} — please upload your ID and sign again.`}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    px={2}
                    py={1}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Iconify icon="solar:document-text-bold" width={18} />
                      <Typography variant="subtitle2">{agreement ? 'Trailer Lease Agreement' : 'Trailer Rental Agreement'}</Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener"
                      endIcon={<Iconify icon="eva:external-link-fill" width={16} />}
                    >
                      Open PDF
                    </Button>
                  </Stack>

                  <Box
                    component="object"
                    data={pdfUrl}
                    type="application/pdf"
                    sx={{ width: '100%', height: '68vh', display: { xs: 'none', md: 'block' } }}
                  >
                    <Box p={3}>
                      <Typography color="text.secondary">
                        Your browser cannot display the agreement inline — use Open PDF above.
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: { xs: 'flex', md: 'none' }, p: 3, justifyContent: 'center' }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener"
                      startIcon={<Iconify icon="solar:document-text-bold" />}
                    >
                      Read the full agreement
                    </Button>
                  </Box>
                </Box>

                {signerFields.length > 0 && (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 2,
                    }}
                  >
                    <Stack gap={1.5}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Iconify
                          icon={
                            allSignerFieldsFilled ? 'solar:check-circle-bold' : 'solar:pen-new-square-bold'
                          }
                          width={24}
                          color={allSignerFieldsFilled ? 'success.main' : 'text.secondary'}
                        />
                        <Box>
                          <Typography variant="subtitle2">Your information *</Typography>
                          <Typography variant="body2" color="text.secondary">
                            The agreement requires the following details. They will appear in the
                            signed document.
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack gap={1.5}>
                        {signerFields.map((name) => (
                          <TextField
                            key={name}
                            label={name}
                            fullWidth
                            value={signerValue(name)}
                            onChange={(event) =>
                              setSignerValues((values) => ({ ...values, [name]: event.target.value }))
                            }
                            inputProps={{ maxLength: 200 }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                )}

                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    gap={1.5}
                  >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Iconify
                        icon={idPhotoCount > 0 ? 'solar:check-circle-bold' : 'solar:user-id-bold'}
                        width={24}
                        color={idPhotoCount > 0 ? 'success.main' : 'text.secondary'}
                      />
                      <Box>
                        <Typography variant="subtitle2">Photo of your ID *</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {idPhotoCount > 0
                            ? `${idPhotoCount} photo${idPhotoCount > 1 ? 's' : ''} uploaded`
                            : "A photo of your driver's license or ID card is required before signing"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      variant={idPhotoCount > 0 ? 'outlined' : 'contained'}
                      component="label"
                      disabled={uploadingId}
                      startIcon={
                        uploadingId ? <CircularProgress size={16} /> : <Iconify icon="solar:camera-bold" />
                      }
                    >
                      {idPhotoCount > 0 ? 'Add another photo' : 'Upload ID photo'}
                      <input
                        hidden
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            uploadIdPhoto(file);
                          }
                          // allow re-selecting the same file
                          event.target.value = '';
                        }}
                      />
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Container>
          )}
        </DisplayPageData>
      </Box>

      {!doneSigning && loaded && contractInfo?.status === 'pending' && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -4px 12px rgba(1, 14, 7, 0.06)',
          }}
        >
          <Container maxWidth="md">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              gap={1}
              py={1.5}
            >
              <FormControlLabel
                control={
                  <Checkbox checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
                }
                label={
                  <Typography variant="body2">I have read and agree to the rental agreement</Typography>
                }
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={!agreed || idPhotoCount === 0 || !allSignerFieldsFilled}
                onClick={() => setSignatureOpen(true)}
                startIcon={<Iconify icon="solar:pen-bold" />}
                sx={{ minWidth: 200 }}
              >
                Sign Agreement
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      <Dialog open={signatureOpen} onClose={() => !submitting && setSignatureOpen(false)} maxWidth="sm">
        <DialogTitle>Draw your signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'common.white' }}>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: canvasWidth, height: 200 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Sign with your finger or mouse
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => sigCanvas.current?.clear()} disabled={submitting}>
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitSignature}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Iconify icon="solar:pen-bold" />}
          >
            Sign Agreement
          </Button>
        </DialogActions>
      </Dialog>

      <Box py={2} textAlign="center">
        <Typography variant="caption" color="text.disabled">
          Powered by Counting Trucks
        </Typography>
      </Box>
    </Box>
  );
}
